import type { CapitalReport } from './types';

export const G4_REPORTS: CapitalReport[] = [
  {
    key: 'G4R1', group: 'G4', rCode: 'R1', domain: 'Operational Readiness',
    stage: 'Foundation Stage (Governance Risk Phase)', tone: 'red', profile: 'Equity Novice',
    intro: "This report evaluates your startup's governance and company structure readiness.",
    sections: [
      { title: 'Executive Summary', blocks: [
        { kind: 'paragraph', text: 'You are at the very beginning of your governance and company structure journey.' },
        { kind: 'paragraph', text: 'At this stage, your startup may already have: a strong idea; early traction; a motivated founding team' },
        { kind: 'paragraph', text: 'However: Your company lacks the legal and structural foundation required to operate safely and scale' },
        { kind: 'paragraph', text: 'Key Reality: A great product cannot compensate for weak structure; Founder disputes can destroy value instantly; Investors will not engage without governance clarity' },
        { kind: 'paragraph', text: 'From an investor perspective: Your startup is currently high risk due to lack of structure' },
        { kind: 'callout', text: 'You are operating in: The Governance Risk Phase' },
      ]},
      { title: 'Operational Readiness Decision', blocks: [
        { kind: 'table', headers: ['Metric', 'Assessment'], rows: [
          [{ text: 'Governance Level' }, { text: 'Not Structured', tone: 'red' }],
          [{ text: 'Equity Clarity' }, { text: 'Undefined / unclear', tone: 'red' }],
          [{ text: 'Cap Table' }, { text: 'Incomplete / inaccurate', tone: 'orange' }],
          [{ text: 'Roles & Responsibilities' }, { text: 'Not defined', tone: 'red' }],
          [{ text: 'Decision-Making' }, { text: 'Ad hoc', tone: 'red' }],
          [{ text: 'Legal / Compliance' }, { text: 'Missing', tone: 'red' }],
          [{ text: 'Risk Level' }, { text: 'Very High', tone: 'red' }],
          [{ text: 'Investor Readiness' }, { text: 'Not Investable', tone: 'red' }],
        ]},
      ]},
      { title: 'Governance Gap Analysis', blocks: [
        { kind: 'paragraph', text: 'Current State vs Required for Basic Credibility' },
        { kind: 'table', headers: ['Area', 'Your Current Status', 'Required to Progress'], rows: [
          [{ text: 'Founder Agreements' }, { text: 'None', tone: 'red' }, { text: 'Signed & structured' }],
          [{ text: 'Cap Table' }, { text: 'Unclear', tone: 'orange' }, { text: 'Clean & accurate' }],
          [{ text: 'Roles' }, { text: 'Undefined', tone: 'red' }, { text: 'Clearly assigned' }],
          [{ text: 'Decision Rights' }, { text: 'Missing', tone: 'red' }, { text: 'Documented' }],
          [{ text: 'Governance' }, { text: 'None', tone: 'red' }, { text: 'Basic structure' }],
          [{ text: 'Legal Documents' }, { text: 'Missing', tone: 'red' }, { text: 'Contracts & IP assignment' }],
        ]},
        { kind: 'paragraph', text: 'You have a startup — but no foundation' },
      ]},
      { title: 'Why You Are in This Stage', blocks: [
        { kind: 'paragraph', text: 'Based on your diagnostic inputs:' },
        { kind: 'bullets', items: [
          'No formal shareholder or founder agreements',
          'Governance is informal or non-existent',
          'Roles and responsibilities are unclear',
          'Cap table may be incomplete or inaccurate',
          'No compliance or structured policies',
        ]},
        { kind: 'callout', text: 'This leads to: "High potential — high structural risk"' },
      ]},
      { title: 'Critical Risks Identified', blocks: [
        { kind: 'steps', items: [
          { title: 'Founder Conflict Risk', sub: ['Without agreements: disputes are inevitable', 'equity misunderstandings can destroy the company'] },
          { title: 'Cap Table Risk', sub: ['If ownership is unclear: investors will not proceed', 'due diligence will fail'] },
          { title: 'IP Ownership Risk', sub: ['If IP is not assigned: company does not legally own its product'] },
          { title: 'Legal & Compliance Risk', sub: ['Without structure: contracts are weak', 'liabilities are unclear', 'growth becomes dangerous'] },
        ]},
      ]},
      { title: 'Strategic Priorities (Next 30–60 Days)', blocks: [
        { kind: 'steps', items: [
          { title: 'Clarify Equity & Ownership (CRITICAL)', sub: ['Draft founder/shareholder agreements', 'Build a clean cap table', 'Define vesting schedules. Goal: Align incentives and prevent future conflict'] },
          { title: 'Define Roles & Decision Rights', sub: ['Assign clear responsibilities', 'Define decision-making authority', 'Document who owns what. Goal: Eliminate ambiguity and improve execution'] },
          { title: 'Establish Governance Basics', sub: ['Incorporate your company (if not done)', 'Create simple board/advisory structure', 'Draft: employment contracts; IP assignment; key policies. Goal: Build minimum viable governance'] },
        ]},
      ]},
      { title: 'What This Means for You', blocks: [
        { kind: 'paragraph', text: 'You are building your company on: potential — not structure' },
        { kind: 'paragraph', text: 'Without fixing this: growth will be fragile; investors will not engage; risk will increase over time' },
        { kind: 'paragraph', text: 'At this stage: Structure = survival' },
        { kind: 'callout', text: 'Your evolution: From "idea + team" to "company + foundation"' },
      ]},
      { title: 'Your Governance Evolution Path', blocks: [
        { kind: 'path', steps: ['No Structure', 'Equity Novice', 'Structure Starter', 'Governance Builder', 'Investor-Ready Organization'], currentIndex: 1 },
      ]},
      { title: 'Recommended Next Step', blocks: [
        { kind: 'paragraph', text: 'Recommended Path: Governance Kickstart Package' },
        { kind: 'callout', text: 'Governance Kickstart Package' },
        { kind: 'paragraph', text: 'We help you:' },
        { kind: 'bullets', items: [
          'structure your cap table',
          'draft founder agreements',
          'define roles and ownership',
          'establish governance basics',
        ]},
        { kind: 'paragraph', text: 'Outcome: From risky startup → credible company' },
      ]},
      { title: 'Immediate Action Plan', blocks: [
        { kind: 'bullets', items: [
          'Draft founder/shareholder agreement',
          'Build clean cap table',
          'Assign roles and responsibilities',
          'Formalize IP ownership',
          'Create basic governance structure',
        ]},
      ]},
      { title: 'Final CTA', blocks: [
        { kind: 'callout', text: 'Fix your foundation before you scale your business', tone: 'green' },
      ]},
    ],
  },
  {
    key: 'G4R2', group: 'G4', rCode: 'R2', domain: 'Operational Readiness',
    stage: 'Governance Development Stage (Credibility Phase)', tone: 'yellow', profile: 'Governance Builder',
    intro: "This report evaluates your startup's governance and company structure readiness.",
    sections: [
      { title: 'Executive Summary', blocks: [
        { kind: 'paragraph', text: 'You have made real and meaningful progress in structuring your company.' },
        { kind: 'paragraph', text: 'At this stage, your business now has: a formal legal structure (incorporation); a cleaner cap table; early contracts and agreements; initial governance elements (possibly advisory/board structure)' },
        { kind: 'paragraph', text: 'This is a major upgrade from early-stage startups' },
        { kind: 'paragraph', text: 'However: Your governance is still not reliable enough for investors' },
        { kind: 'bullets', items: [
          'structures exist — but are not fully formalized',
          'governance is present — but not consistent',
          'documentation exists — but not standardized',
        ]},
        { kind: 'paragraph', text: 'From an investor perspective: "You are progressing — but still not due diligence ready"' },
        { kind: 'callout', text: 'You are currently in: The Governance Development Phase' },
      ]},
      { title: 'Operational Readiness Decision', blocks: [
        { kind: 'table', headers: ['Metric', 'Assessment'], rows: [
          [{ text: 'Governance Level' }, { text: 'Developing', tone: 'yellow' }],
          [{ text: 'Legal Structure' }, { text: 'Established', tone: 'green' }],
          [{ text: 'Cap Table' }, { text: 'Clean but not optimized', tone: 'yellow' }],
          [{ text: 'Board / Advisory' }, { text: 'Informal', tone: 'orange' }],
          [{ text: 'Contracts' }, { text: 'Partial standardization', tone: 'orange' }],
          [{ text: 'Policies' }, { text: 'Inconsistent', tone: 'orange' }],
          [{ text: 'Risk Level' }, { text: 'Medium–High', tone: 'yellow' }],
          [{ text: 'Investor Readiness' }, { text: 'Not yet ready', tone: 'orange' }],
        ]},
      ]},
      { title: 'Governance Gap Analysis', blocks: [
        { kind: 'paragraph', text: 'Current State vs Required for Investor Credibility' },
        { kind: 'table', headers: ['Area', 'Your Current Status', 'Required to Progress'], rows: [
          [{ text: 'Cap Table' }, { text: 'Clean', tone: 'yellow' }, { text: 'Advanced modeling (ESOP, dilution)' }],
          [{ text: 'Governance Structure' }, { text: 'Exists', tone: 'orange' }, { text: 'Formal board processes' }],
          [{ text: 'Meetings' }, { text: 'Irregular', tone: 'red' }, { text: 'Structured cadence + minutes' }],
          [{ text: 'Contracts' }, { text: 'Partial', tone: 'orange' }, { text: 'Fully standardized' }],
          [{ text: 'Policies' }, { text: 'Incomplete', tone: 'red' }, { text: 'Full governance framework' }],
          [{ text: 'Compliance' }, { text: 'Reactive', tone: 'orange' }, { text: 'Proactive system' }],
        ]},
        { kind: 'paragraph', text: 'You have structure — but not yet discipline' },
      ]},
      { title: 'Why You Are in This Stage', blocks: [
        { kind: 'paragraph', text: 'Based on your report:' },
        { kind: 'bullets', items: [
          'Cap table exists but lacks advanced planning (ESOP, dilution scenarios)',
          'Advisory/board structures may exist but lack: cadence; agendas; minutes',
          'Founder agreements and IP assignments exist BUT: employee/contractor agreements are not standardized',
          'Governance documentation exists but is: inconsistent; incomplete',
        ]},
        { kind: 'callout', text: 'This creates: "Credibility is emerging — but not yet secured"' },
      ]},
      { title: 'Critical Risks Identified', blocks: [
        { kind: 'steps', items: [
          { title: 'Governance Inconsistency Risk', sub: ['Partial governance creates: confusion', 'lack of accountability', 'weak decision-making'] },
          { title: 'Due Diligence Risk', sub: ['Investors will identify: missing documentation', 'inconsistent contracts', 'lack of governance discipline'] },
          { title: 'Equity Mismanagement Risk', sub: ['Without advanced modeling: dilution surprises', 'ESOP issues', 'investor mistrust'] },
          { title: 'Compliance Risk', sub: ['Lack of systems leads to: missed filings', 'legal exposure', 'operational inefficiency'] },
        ]},
      ]},
      { title: 'Strategic Priorities (Next 60–90 Days)', blocks: [
        { kind: 'steps', items: [
          { title: 'Strengthen Equity Management (CRITICAL)', sub: ['Finalize ESOP structure: pool size; vesting terms', 'Model: future dilution scenarios; funding rounds impact', 'Ensure: all equity agreements are consistent and enforceable. Goal: Strategic ownership clarity (investor-ready)'] },
          { title: 'Build Reliable Governance Processes', sub: ['Formalize: board / advisory meetings', 'Define: cadence (monthly / quarterly); agendas; minutes', 'Create: compliance calendar (filings; tax; IP renewals)', 'Assign: governance ownership. Goal: Consistency + accountability'] },
          { title: 'Standardize Legal & HR Documentation', sub: ['Implement standard contracts for: employees; advisors; freelancers', 'Draft / refine: HR policies; ethics & compliance policies; anti-bribery basics; ESG fundamentals', 'Ensure: all IP and data rights belong to the company. Goal: Legal strength + operational protection'] },
        ]},
      ]},
      { title: 'What This Means for You', blocks: [
        { kind: 'paragraph', text: 'You have moved beyond: ad hoc governance' },
        { kind: 'paragraph', text: 'You are now building: the backbone of a credible company' },
        { kind: 'paragraph', text: 'However: Credibility requires consistency' },
        { kind: 'paragraph', text: 'At this stage: incomplete governance = investor concern; inconsistent policies = operational risk' },
        { kind: 'paragraph', text: 'The key shift: Treat governance like product and sales' },
        { kind: 'callout', text: 'When done right: Governance becomes a competitive advantage' },
      ]},
      { title: 'Your Governance Evolution Path', blocks: [
        { kind: 'path', steps: ['Structure Starter', 'Governance Builder', 'Structure Professional', 'Investor-Ready Organization', 'Institutional Company'], currentIndex: 1 },
      ]},
      { title: 'Recommended Next Step', blocks: [
        { kind: 'paragraph', text: 'Recommended Path: Governance System & Investor Readiness Program' },
        { kind: 'callout', text: 'Governance System & Investor Readiness Program' },
        { kind: 'paragraph', text: 'This will help you:' },
        { kind: 'bullets', items: [
          'finalize ESOP and cap table strategy',
          'structure board governance',
          'standardize legal and HR framework',
          'prepare for investor due diligence',
        ]},
        { kind: 'paragraph', text: 'Outcome: From partial governance → investor-grade company' },
      ]},
      { title: 'Immediate Action Plan', blocks: [
        { kind: 'bullets', items: [
          'Finalize ESOP and dilution scenarios',
          'Set board/advisory meeting cadence',
          'Create compliance calendar',
          'Standardize all contracts',
          'Ensure full IP ownership',
        ]},
      ]},
      { title: 'Final CTA', blocks: [
        { kind: 'callout', text: 'Turn your governance into a system investors can trust', tone: 'green' },
      ]},
    ],
  },
  {
    key: 'G4R3', group: 'G4', rCode: 'R3', domain: 'Operational Readiness',
    stage: 'Governance Discipline Stage (Control & Consistency Phase)', tone: 'yellow', profile: 'Structure Professional',
    intro: "This report evaluates your startup's governance and company structure readiness.",
    sections: [
      { title: 'Executive Summary', blocks: [
        { kind: 'paragraph', text: 'You have built a solid governance foundation and are now entering a more advanced stage of operational maturity.' },
        { kind: 'paragraph', text: 'At this stage, your company has: a structured legal entity; a clean and organized cap table; defined roles and responsibilities; basic governance processes in place; standardized contracts and documentation' },
        { kind: 'paragraph', text: 'This place you ahead of most early-stage startups' },
        { kind: 'paragraph', text: 'However: Governance is still not fully institutionalized' },
        { kind: 'bullets', items: [
          'processes exist — but are not consistently enforced',
          'decision-making is structured — but still founder-driven',
          'reporting exists — but not fully standardized',
          'governance is present — but not yet scalable',
        ]},
        { kind: 'paragraph', text: 'From an investor perspective: "This company is structured — but not yet fully controlled"' },
        { kind: 'callout', text: 'You are now in: The Governance Discipline Phase' },
      ]},
      { title: 'Operational Readiness Decision', blocks: [
        { kind: 'table', headers: ['Metric', 'Assessment'], rows: [
          [{ text: 'Governance Level' }, { text: 'Structured', tone: 'yellow' }],
          [{ text: 'Legal Structure' }, { text: 'Fully established', tone: 'green' }],
          [{ text: 'Cap Table' }, { text: 'Clean & organized', tone: 'green' }],
          [{ text: 'Roles & Responsibilities' }, { text: 'Defined', tone: 'green' }],
          [{ text: 'Decision-Making' }, { text: 'Structured but founder-led', tone: 'yellow' }],
          [{ text: 'Board / Advisory' }, { text: 'Active but informal', tone: 'orange' }],
          [{ text: 'Policies & Compliance' }, { text: 'Implemented but inconsistent', tone: 'yellow' }],
          [{ text: 'Risk Level' }, { text: 'Medium', tone: 'yellow' }],
          [{ text: 'Investor Readiness' }, { text: 'Approaching readiness', tone: 'orange' }],
        ]},
      ]},
      { title: 'Governance Gap Analysis', blocks: [
        { kind: 'paragraph', text: 'Current State vs Required for Scalable Governance' },
        { kind: 'table', headers: ['Area', 'Your Current Status', 'Required to Progress'], rows: [
          [{ text: 'Governance Processes' }, { text: 'Defined', tone: 'yellow' }, { text: 'Fully enforced & repeatable' }],
          [{ text: 'Board Structure' }, { text: 'Informal', tone: 'orange' }, { text: 'Formal governance body' }],
          [{ text: 'Meetings' }, { text: 'Occasional', tone: 'yellow' }, { text: 'Fixed cadence + reporting' }],
          [{ text: 'Reporting' }, { text: 'Partial', tone: 'orange' }, { text: 'Standardized dashboards' }],
          [{ text: 'Policies' }, { text: 'Implemented', tone: 'yellow' }, { text: 'Fully enforced system' }],
          [{ text: 'Compliance' }, { text: 'Reactive', tone: 'orange' }, { text: 'Proactive & automated' }],
        ]},
        { kind: 'paragraph', text: 'You have structure — now you need discipline' },
      ]},
      { title: 'Why You Are in This Stage', blocks: [
        { kind: 'bullets', items: [
          'Governance systems exist but are not consistently applied',
          'Decision-making still depends on founders',
          'Board/advisory involvement is informal',
          'Reporting is inconsistent or manual',
          'Compliance is reactive rather than planned',
        ]},
        { kind: 'callout', text: 'This creates: "Structure exists — but discipline is not yet embedded"' },
      ]},
      { title: 'Critical Risks Identified', blocks: [
        { kind: 'steps', items: [
          { title: 'Founder Dependency Risk', sub: ['Governance relies on individuals, not systems'] },
          { title: 'Inconsistent Execution', sub: ['Processes exist but are not followed consistently'] },
          { title: 'Weak Governance Signaling', sub: ['Investors may see: lack of rigor', 'lack of control', 'limited scalability'] },
          { title: 'Scaling Friction', sub: ['Without discipline: operations slow down', 'decision-making becomes inconsistent', 'execution quality drops'] },
        ]},
      ]},
      { title: 'Strategic Priorities (Next 60–90 Days)', blocks: [
        { kind: 'steps', items: [
          { title: 'Enforce Governance Discipline (CRITICAL)', sub: ['Apply governance processes consistently', 'Ensure all decisions follow structure', 'Document and track execution. Goal: From process → habit'] },
          { title: 'Formalize Board & Decision Structure', sub: ['Define formal board/advisory roles', 'Establish clear governance authority', 'Align strategic vs operational decisions. Goal: Clarity + accountability'] },
          { title: 'Standardize Reporting Systems', sub: ['Implement consistent reporting cadence', 'Build dashboards for: operations; finance; growth. Goal: Visibility + control'] },
          { title: 'Move to Proactive Compliance', sub: ['Build compliance calendar', 'Automate key governance tasks', 'Assign ownership. Goal: No surprises'] },
        ]},
      ]},
      { title: 'What This Means for You', blocks: [
        { kind: 'paragraph', text: 'You are no longer building structure. You are building discipline' },
        { kind: 'paragraph', text: 'At this stage: structure gives credibility; discipline gives control; control enables scale' },
        { kind: 'callout', text: 'The key shift: From "organized startup" → "managed company"' },
      ]},
      { title: 'Your Governance Evolution Path', blocks: [
        { kind: 'path', steps: ['Governance Builder', 'Structure Professional', 'Investor-Ready Organization', 'Institutional Company'], currentIndex: 1 },
      ]},
      { title: 'Recommended Next Step', blocks: [
        { kind: 'paragraph', text: 'Recommended Path: Governance Optimization & Control Program' },
        { kind: 'callout', text: 'Governance Optimization & Control Program' },
        { kind: 'paragraph', text: 'This will help you:' },
        { kind: 'bullets', items: [
          'enforce governance systems',
          'structure board and decision-making',
          'standardize reporting',
          'prepare for investor due diligence',
        ]},
        { kind: 'paragraph', text: 'Outcome: From structured company → controlled and scalable organization' },
      ]},
      { title: 'Immediate Action Plan', blocks: [
        { kind: 'bullets', items: [
          'Enforce governance processes across all functions',
          'Formalize board/advisory structure',
          'Implement reporting cadence',
          'Build compliance calendar',
          'Assign governance ownership',
        ]},
      ]},
      { title: 'Final CTA', blocks: [
        { kind: 'callout', text: 'Turn your structure into discipline — and your company into a controllable system', tone: 'green' },
      ]},
    ],
  },
  {
    key: 'G4R4', group: 'G4', rCode: 'R4', domain: 'Operational Readiness',
    stage: 'Professional Governance Stage (Systemization Phase)', tone: 'yellow', profile: 'Governance Professional',
    intro: "This report evaluates your startup's governance and company structure readiness.",
    sections: [
      { title: 'Executive Summary', blocks: [
        { kind: 'paragraph', text: 'You have reached a strong and credible level of governance maturity.' },
        { kind: 'paragraph', text: 'Your company now demonstrates: a clean and accurate cap table; standardized agreements (founders, employees, advisors); defined governance processes; an active board or advisory structure; basic compliance and policy framework' },
        { kind: 'paragraph', text: 'This places you ahead of most startups' },
        { kind: 'paragraph', text: 'From an external perspective: investors take you seriously; partners trust your structure; your company appears professionally managed' },
        { kind: 'paragraph', text: 'However: Your governance is not yet scalable' },
        { kind: 'bullets', items: [
          'processes exist — but are not fully systemized',
          'governance is structured — but not optimized',
          'compliance is present — but not fully tracked',
          'reporting exists — but lacks consistency',
        ]},
        { kind: 'callout', text: 'You are currently in: The Governance Systemization Phase' },
      ]},
      { title: 'Operational Readiness Decision', blocks: [
        { kind: 'table', headers: ['Metric', 'Assessment'], rows: [
          [{ text: 'Governance Level' }, { text: 'Professional', tone: 'yellow' }],
          [{ text: 'Cap Table' }, { text: 'Clean & structured', tone: 'green' }],
          [{ text: 'Equity Planning' }, { text: 'Defined but not optimized', tone: 'yellow' }],
          [{ text: 'Board Governance' }, { text: 'Active', tone: 'yellow' }],
          [{ text: 'Policies' }, { text: 'Implemented', tone: 'yellow' }],
          [{ text: 'Compliance' }, { text: 'Partial tracking', tone: 'orange' }],
          [{ text: 'Reporting' }, { text: 'Inconsistent', tone: 'orange' }],
          [{ text: 'Risk Level' }, { text: 'Medium', tone: 'yellow' }],
          [{ text: 'Investor Readiness' }, { text: 'Near-ready', tone: 'orange' }],
        ]},
      ]},
      { title: 'Governance Gap Analysis', blocks: [
        { kind: 'paragraph', text: 'Current State vs Required for Scalable Governance' },
        { kind: 'table', headers: ['Area', 'Your Current Status', 'Required to Progress'], rows: [
          [{ text: 'Governance Processes' }, { text: 'Defined', tone: 'yellow' }, { text: 'Fully systemized' }],
          [{ text: 'Board Meetings' }, { text: 'Active', tone: 'yellow' }, { text: 'Structured cadence + reporting' }],
          [{ text: 'Equity Management' }, { text: 'Clear', tone: 'yellow' }, { text: 'Optimized & modeled' }],
          [{ text: 'Compliance' }, { text: 'Partial', tone: 'orange' }, { text: 'Fully tracked' }],
          [{ text: 'Policies' }, { text: 'Implemented', tone: 'yellow' }, { text: 'Fully enforced' }],
          [{ text: 'Reporting' }, { text: 'Inconsistent', tone: 'orange' }, { text: 'Standardized dashboards' }],
        ]},
        { kind: 'paragraph', text: 'You are credible — but not yet scalable' },
      ]},
      { title: 'Why You Are in This Stage', blocks: [
        { kind: 'bullets', items: [
          'Governance systems exist and are used',
          'Board/advisory is active',
          'Contracts and legal structure are standardized',
          'Compliance exists but is not fully tracked',
          'Reporting is inconsistent or manual',
        ]},
        { kind: 'callout', text: 'This creates: "Professional company — but not yet a system-driven organization"' },
      ]},
      { title: 'Critical Risks Identified', blocks: [
        { kind: 'steps', items: [
          { title: 'Governance Scalability Risk', sub: ['Structure works now — but may break under growth'] },
          { title: 'Inconsistent Governance Execution', sub: ['Processes exist but are not always followed'] },
          { title: 'Reporting Gap', sub: ['Lack of standard reporting reduces investor confidence'] },
          { title: 'Compliance Visibility Risk', sub: ['Without tracking: gaps may go unnoticed', 'risks may accumulate'] },
        ]},
      ]},
      { title: 'Strategic Priorities (Next 60–90 Days)', blocks: [
        { kind: 'steps', items: [
          { title: 'Systemize Governance Processes (CRITICAL)', sub: ['Build governance calendar', 'Define meeting cadence', 'Standardize agendas & minutes', 'Assign governance ownership. Goal: From structure → system'] },
          { title: 'Standardize Reporting', sub: ['Implement consistent reporting cadence', 'Build dashboards for: governance; finance; operations. Goal: Visibility + control'] },
          { title: 'Strengthen Compliance Tracking', sub: ['Track all compliance activities', 'Document processes', 'Prepare audit-ready structure. Goal: No blind spots'] },
        ]},
      ]},
      { title: 'What This Means for You', blocks: [
        { kind: 'paragraph', text: 'You are no longer fixing governance. You are preparing it to scale' },
        { kind: 'paragraph', text: 'At this stage: structure builds credibility; systems enable scale' },
        { kind: 'paragraph', text: 'The key shift: From "professional company" to "system-driven organization"' },
      ]},
      { title: 'Your Governance Evolution Path', blocks: [
        { kind: 'path', steps: ['Structure Professional', 'Governance Professional', 'Governance Strategist', 'Investor-Ready Organization', 'Institutional Company'], currentIndex: 1 },
      ]},
      { title: 'Recommended Next Step', blocks: [
        { kind: 'paragraph', text: 'Recommended Path: Governance System & Optimization Program' },
        { kind: 'callout', text: 'Governance System & Optimization Program' },
        { kind: 'paragraph', text: 'This will help you:' },
        { kind: 'bullets', items: [
          'systemize governance processes',
          'standardize reporting',
          'strengthen compliance tracking',
          'prepare for scaling',
        ]},
        { kind: 'paragraph', text: 'Outcome: From professional structure → scalable governance system' },
      ]},
      { title: 'Immediate Action Plan', blocks: [
        { kind: 'bullets', items: [
          'Build governance calendar',
          'Standardize reporting',
          'Track compliance activities',
          'Formalize board cadence',
          'Assign governance ownership',
        ]},
      ]},
      { title: 'Final CTA', blocks: [
        { kind: 'callout', text: 'Turn your governance into a scalable system that supports growth', tone: 'green' },
      ]},
    ],
  },
  {
    key: 'G4R5', group: 'G4', rCode: 'R5', domain: 'Operational Readiness',
    stage: 'Strategic Governance Stage (Alignment & Leverage Phase)', tone: 'green', profile: 'Governance Strategist',
    intro: "This report evaluates your startup's governance and company structure readiness.",
    sections: [
      { title: 'Executive Summary', blocks: [
        { kind: 'paragraph', text: 'You have built strong governance foundations.' },
        { kind: 'paragraph', text: 'Your company now demonstrates: a clean and well-structured cap table; ESOP planning and dilution modeling; standardized contracts and agreements; a functioning board or advisory structure; consistent compliance tracking' },
        { kind: 'paragraph', text: 'This is a high-quality governance level' },
        { kind: 'paragraph', text: 'You are no longer fixing governance — you are leveraging it' },
        { kind: 'paragraph', text: 'At this stage: governance builds trust; governance supports decision-making; governance strengthens investor confidence' },
        { kind: 'paragraph', text: 'However: You are not yet fully institutional' },
        { kind: 'bullets', items: [
          'governance is credible — but not yet strategic at scale',
          'processes exist — but are not fully optimized',
          'ESG and advanced policies are still developing',
        ]},
        { kind: 'callout', text: 'You are currently in: The Strategic Governance Phase' },
      ]},
      { title: 'Operational Readiness Decision', blocks: [
        { kind: 'table', headers: ['Metric', 'Assessment'], rows: [
          [{ text: 'Governance Level' }, { text: 'Strategic', tone: 'green' }],
          [{ text: 'Cap Table & ESOP' }, { text: 'Strong & modeled', tone: 'green' }],
          [{ text: 'Board Governance' }, { text: 'Functional', tone: 'yellow' }],
          [{ text: 'Compliance' }, { text: 'Consistent', tone: 'green' }],
          [{ text: 'ESG & Advanced Policies' }, { text: 'Developing', tone: 'orange' }],
          [{ text: 'Reporting' }, { text: 'Improving', tone: 'yellow' }],
          [{ text: 'Risk Level' }, { text: 'Medium', tone: 'yellow' }],
          [{ text: 'Investor Readiness' }, { text: 'Strong (early-stage investors)', tone: 'green' }],
        ]},
      ]},
      { title: 'Governance Gap Analysis', blocks: [
        { kind: 'paragraph', text: 'Current State vs Required for Institutional Investment' },
        { kind: 'table', headers: ['Area', 'Your Current Status', 'Required to Progress'], rows: [
          [{ text: 'Board Governance' }, { text: 'Functional', tone: 'yellow' }, { text: 'Institutional structure' }],
          [{ text: 'ESOP' }, { text: 'Strong', tone: 'green' }, { text: 'Fully optimized & benchmarked' }],
          [{ text: 'Compliance' }, { text: 'Consistent', tone: 'green' }, { text: 'Automated & scalable' }],
          [{ text: 'ESG' }, { text: 'Basic', tone: 'orange' }, { text: 'Advanced frameworks' }],
          [{ text: 'Reporting' }, { text: 'Present', tone: 'yellow' }, { text: 'Investor-grade standardization' }],
        ]},
        { kind: 'paragraph', text: 'You are credible — but not yet institutional-grade' },
      ]},
      { title: 'Why You Are in This Stage', blocks: [
        { kind: 'bullets', items: [
          'Cap table and ESOP are strong, with dilution modeling in place',
          'Board structure exists with agendas and minutes',
          'Governance is credible to early investors',
          'Policies cover HR, ethics, compliance, and IP',
          'Compliance tracking is consistent, but not automated',
        ]},
        { kind: 'callout', text: 'This creates: "Strong governance — but not yet a strategic system at scale"' },
      ]},
      { title: 'Critical Risks Identified', blocks: [
        { kind: 'steps', items: [
          { title: 'Governance Plateau Risk', sub: ['Governance may look "good enough," but top-tier investors expect more'] },
          { title: 'Lack of Institutional Depth', sub: ['Missing: structured board governance', 'external investor involvement', 'advanced reporting discipline'] },
          { title: 'ESG & Policy Gap', sub: ['Without stronger ESG and advanced policy framework: institutional investors may hesitate', 'strategic partners may see limitations'] },
          { title: 'Governance Narrative Weakness', sub: ['Governance must not only exist — it must be communicated as part of your strategic story'] },
        ]},
      ]},
      { title: 'Strategic Priorities (Next 60–90 Days)', blocks: [
        { kind: 'steps', items: [
          { title: 'Elevate Board Governance (CRITICAL)', sub: ['Move from founder-led governance to structured governance', 'Include external advisors and strategic stakeholders', 'Standardize board decks and reporting format. Goal: Strategic decision-making + investor confidence'] },
          { title: 'Advance Equity & ESOP Management', sub: ['Formalize ESOP grants for employees', 'Benchmark ESOP against market standards', 'Prepare complete equity documentation. Goal: Competitive and scalable ownership structure'] },
          { title: 'Expand Compliance & ESG Framework', sub: ['Introduce stronger ESG frameworks', 'Document anti-corruption, diversity, sustainability, and ethics policies', 'Build a governance narrative for investors and partners. Goal: Institutional credibility'] },
        ]},
      ]},
      { title: 'What This Means for You', blocks: [
        { kind: 'paragraph', text: 'You are no longer building governance for compliance. You are building governance for growth' },
        { kind: 'paragraph', text: 'At this stage, governance now impacts: investor confidence; employee trust; strategic decisions' },
        { kind: 'paragraph', text: 'Governance becomes a growth multiplier' },
        { kind: 'callout', text: 'Your evolution: From "structured company" to "strategically governed organization"' },
      ]},
      { title: 'Your Governance Evolution Path', blocks: [
        { kind: 'path', steps: ['Governance Professional', 'Governance Strategist', 'Investor-Ready Organization', 'Institutional Company'], currentIndex: 1 },
      ]},
      { title: 'Recommended Next Step', blocks: [
        { kind: 'paragraph', text: 'Recommended Path: Strategic Governance & Investor Alignment Program' },
        { kind: 'callout', text: 'Strategic Governance & Investor Alignment Program' },
        { kind: 'paragraph', text: 'This will help you:' },
        { kind: 'bullets', items: [
          'strengthen investor-grade governance',
          'align governance with fundraising',
          'improve board strategy',
          'position governance as a strategic asset',
        ]},
        { kind: 'paragraph', text: 'Outcome: From strong governance → investor-aligned strategic organization' },
      ]},
      { title: 'Immediate Action Plan', blocks: [
        { kind: 'bullets', items: [
          'Upgrade board governance structure',
          'Standardize board reporting',
          'Finalize ESOP grants',
          'Expand ESG and advanced policies',
          'Build governance narrative for investors',
        ]},
      ]},
      { title: 'Final CTA', blocks: [
        { kind: 'callout', text: 'Turn governance into a strategic advantage that attracts investors and strengthens long-term growth', tone: 'green' },
      ]},
    ],
  },
  {
    key: 'G4R6', group: 'G4', rCode: 'R6', domain: 'Operational Readiness',
    stage: 'Institutional Governance Stage (Investor Confidence Phase)', tone: 'green', profile: 'Governance Leader',
    intro: "This report evaluates your startup's governance and company structure readiness.",
    sections: [
      { title: 'Executive Summary', blocks: [
        { kind: 'paragraph', text: 'You have reached a top-tier level of governance maturity.' },
        { kind: 'paragraph', text: 'Your company now demonstrates: a clean, transparent, and well-managed cap table; structured ESOP and equity governance; a formalized board structure with cadence, agendas, and minutes; consistent compliance across tax, legal, HR, data privacy; investor-aligned reporting practices; comprehensive policies including ESG and ethics' },
        { kind: 'paragraph', text: 'This is a rare level of discipline' },
        { kind: 'paragraph', text: 'You now operate like an institutional-grade organization' },
        { kind: 'paragraph', text: 'At this stage: governance is no longer support; governance is a strategic advantage' },
        { kind: 'callout', text: 'You are currently in: The Institutional Governance Phase' },
      ]},
      { title: 'Operational Readiness Decision', blocks: [
        { kind: 'table', headers: ['Metric', 'Assessment'], rows: [
          [{ text: 'Governance Level' }, { text: 'Institutional', tone: 'green' }],
          [{ text: 'Cap Table & ESOP' }, { text: 'Clean, transparent, optimized', tone: 'green' }],
          [{ text: 'Board Governance' }, { text: 'Formalized & structured', tone: 'green' }],
          [{ text: 'Compliance Systems' }, { text: 'Consistent & reliable', tone: 'green' }],
          [{ text: 'Reporting' }, { text: 'Investor-grade', tone: 'green' }],
          [{ text: 'ESG & Policies' }, { text: 'Comprehensive', tone: 'green' }],
          [{ text: 'Risk Level' }, { text: 'Low', tone: 'green' }],
          [{ text: 'Investor Readiness' }, { text: 'Fully ready', tone: 'green' }],
        ]},
      ]},
      { title: 'Governance Gap Analysis', blocks: [
        { kind: 'paragraph', text: 'Current State vs Required for Scaling Institutions' },
        { kind: 'table', headers: ['Area', 'Your Current Status', 'Required to Progress'], rows: [
          [{ text: 'Governance Systems' }, { text: 'Strong', tone: 'green' }, { text: 'Must scale with complexity' }],
          [{ text: 'Board Structure' }, { text: 'Formal', tone: 'green' }, { text: 'Add independence & depth' }],
          [{ text: 'Risk Management' }, { text: 'Developing', tone: 'orange' }, { text: 'Fully structured frameworks' }],
          [{ text: 'Reporting' }, { text: 'Strong', tone: 'green' }, { text: 'Expand governance KPIs' }],
          [{ text: 'ESG' }, { text: 'Implemented', tone: 'green' }, { text: 'Deepen integration' }],
        ]},
        { kind: 'paragraph', text: 'You are investor-ready — now you must scale governance with growth' },
      ]},
      { title: 'Why You Are in This Stage', blocks: [
        { kind: 'bullets', items: [
          'Cap table and ESOP are clean, transparent, and well-managed',
          'Reporting is regular, accurate, and aligned with investor expectations',
          'Board structure is formalized with cadence, agendas, and minutes',
          'Compliance systems are consistent across all key functions',
          'Policies extend to ESG and ethics',
        ]},
        { kind: 'callout', text: 'This confirms: "You are a company institutional investors can trust and scale with"' },
      ]},
      { title: 'Critical Risks Identified', blocks: [
        { kind: 'steps', items: [
          { title: 'Overconfidence Risk (MAIN RISK)', sub: ['The biggest risk is slowing governance evolution'] },
          { title: 'Governance Scalability Risk', sub: ['As the company grows: complexity increases', 'governance must evolve accordingly'] },
          { title: 'Lack of Independent Oversight', sub: ['Without independent directors: decision-making may lack objectivity'] },
          { title: 'Risk Management Gap', sub: ['Without structured frameworks: operational risk increases', 'scaling becomes fragile'] },
        ]},
      ]},
      { title: 'Strategic Priorities (Next 90–120 Days)', blocks: [
        { kind: 'steps', items: [
          { title: 'Institutionalize Investor-Grade Governance (CRITICAL)', sub: ['Adopt quarterly board reporting cadence', 'Standardize metrics across: financial; governance; risk; ESG', 'Prepare for audits and investor due diligence. Goal: Institutional credibility at scale'] },
          { title: 'Strengthen Long-Term Governance Systems', sub: ['Add: independent advisors; non-executive directors', 'Implement: risk management frameworks; escalation processes', 'Create governance continuity plans: succession; crisis management. Goal: Resilience + long-term sustainability'] },
          { title: 'Align Governance With Scaling Needs', sub: ['Adapt ESOP for growth-stage hiring', 'Prepare governance documentation for future funding rounds', 'Build governance narrative: future-focused; investor-aligned. Goal: Governance that supports scaling and valuation'] },
        ]},
      ]},
      { title: 'What This Means for You', blocks: [
        { kind: 'paragraph', text: 'Governance is now a competitive advantage' },
        { kind: 'paragraph', text: 'Investors now see: trust; discipline; maturity; scalability' },
        { kind: 'paragraph', text: 'You are no longer proving readiness. You are proving leadership' },
        { kind: 'paragraph', text: 'Your next challenge: "Can governance scale as fast as your business?"' },
        { kind: 'callout', text: 'Your evolution: From "investor-ready" to "institutional-grade organization"' },
      ]},
      { title: 'Your Governance Evolution Path', blocks: [
        { kind: 'path', steps: ['Governance Strategist', 'Governance Leader', 'Governance Champion', 'Institutional Excellence', 'Global Leadership'], currentIndex: 1 },
      ]},
      { title: 'Recommended Next Step', blocks: [
        { kind: 'paragraph', text: 'Recommended Path: Strategic Governance & Capital Optimization Program' },
        { kind: 'callout', text: 'Strategic Governance & Capital Optimization Program' },
        { kind: 'paragraph', text: 'This will help you:' },
        { kind: 'bullets', items: [
          'align governance with large-scale fundraising',
          'structure an independent board',
          'implement risk frameworks',
          'position governance for institutional investors',
        ]},
        { kind: 'paragraph', text: 'Outcome: From investor-ready → institutional leader' },
      ]},
      { title: 'Immediate Action Plan', blocks: [
        { kind: 'bullets', items: [
          'Implement quarterly investor reporting',
          'Add independent advisors / board members',
          'Build risk management framework',
          'Prepare audit readiness',
          'Align governance with fundraising strategy',
        ]},
      ]},
      { title: 'Final CTA', blocks: [
        { kind: 'callout', text: 'Scale your governance to match your ambition and unlock institutional capital', tone: 'green' },
      ]},
    ],
  },
  {
    key: 'G4R7', group: 'G4', rCode: 'R7', domain: 'Operational Readiness',
    stage: 'Governance Excellence Stage (Global Leadership Phase)', tone: 'green', profile: 'Governance Champion',
    intro: "This report evaluates your startup's governance and company structure readiness.",
    sections: [
      { title: 'Executive Summary', blocks: [
        { kind: 'paragraph', text: 'You have reached the highest possible level of governance maturity.' },
        { kind: 'paragraph', text: 'Your company is no longer: structuring governance; improving governance; validating governance' },
        { kind: 'paragraph', text: 'You are defining governance standards' },
        { kind: 'paragraph', text: 'At this level, your organization demonstrates: fully automated and benchmarked cap table & ESOP; institutionalized governance reporting (board packs, dashboards); proactive and automated compliance systems; independent, structured, and high-quality board governance; advanced policies covering ESG, ethics, diversity, sustainability, cross-border compliance' },
        { kind: 'paragraph', text: 'As highlighted in your report: "Investors don\'t just trust you — they prefer you"' },
        { kind: 'callout', text: 'You are now operating in: The Governance Excellence Phase' },
      ]},
      { title: 'Operational Readiness Decision', blocks: [
        { kind: 'table', headers: ['Metric', 'Assessment'], rows: [
          [{ text: 'Governance Level' }, { text: 'World-Class', tone: 'green' }],
          [{ text: 'Cap Table & ESOP' }, { text: 'Automated & benchmarked', tone: 'green' }],
          [{ text: 'Board Governance' }, { text: 'Institutional + independent', tone: 'green' }],
          [{ text: 'Compliance' }, { text: 'Proactive & automated', tone: 'green' }],
          [{ text: 'Reporting' }, { text: 'Investor-grade dashboards', tone: 'green' }],
          [{ text: 'ESG & Policies' }, { text: 'Comprehensive & advanced', tone: 'green' }],
          [{ text: 'Risk Level' }, { text: 'Minimal', tone: 'green' }],
          [{ text: 'Investor Positioning' }, { text: 'Preferred (Top-tier investors)', tone: 'green' }],
        ]},
      ]},
      { title: 'Governance Gap Analysis', blocks: [
        { kind: 'paragraph', text: 'Current State vs Next-Level Global Leadership' },
        { kind: 'table', headers: ['Area', 'Your Current Status', 'Next Evolution'], rows: [
          [{ text: 'Governance Systems' }, { text: 'Best-in-class', tone: 'green' }, { text: 'Maintain & scale globally' }],
          [{ text: 'Board' }, { text: 'Strong', tone: 'green' }, { text: 'Expand global expertise' }],
          [{ text: 'Compliance' }, { text: 'Automated', tone: 'green' }, { text: 'Cross-border optimization' }],
          [{ text: 'ESG' }, { text: 'Advanced', tone: 'green' }, { text: 'Thought leadership positioning' }],
          [{ text: 'Reporting' }, { text: 'Institutional', tone: 'green' }, { text: 'Strategic storytelling' }],
        ]},
        { kind: 'paragraph', text: 'You are not improving governance — you are leveraging it as a moat' },
      ]},
      { title: 'Why You Are in This Stage', blocks: [
        { kind: 'paragraph', text: 'Based on your report:' },
        { kind: 'bullets', items: [
          'Cap table and ESOP are automated and benchmarked',
          'Governance reporting is institutionalized (quarterly packs, dashboards)',
          'Compliance is proactive across: tax; filings; IP; ESG',
          'Board governance includes: independent directors; structured cadence',
          'Policies are comprehensive and globally aligned',
        ]},
        { kind: 'callout', text: 'This confirms: "You operate at world-class governance standards"' },
      ]},
      { title: 'Strategic Risks (Elite Level)', blocks: [
        { kind: 'paragraph', text: 'At this stage, risks are not operational — they are strategic and global:' },
        { kind: 'steps', items: [
          { title: 'Scaling Complexity Risk', sub: ['Expansion across: geographies; regulations; investor bases'] },
          { title: 'Governance Maintenance Risk', sub: ['Excellence must be continuously maintained — not assumed'] },
          { title: 'Strategic Dilution Risk', sub: ['Rapid growth + capital = dilution; control challenges'] },
          { title: 'Leadership Scaling Risk', sub: ['Governance must evolve with: new leadership layers; global teams'] },
        ]},
      ]},
      { title: 'Strategic Priorities (Next 90–180 Days)', blocks: [
        { kind: 'steps', items: [
          { title: 'Elevate Governance as a Strategic Asset (CRITICAL)', sub: ['Integrate governance into investor narrative', 'Publish: ESG reports; governance insights', 'Use governance to: attract top-tier investors; secure global partnerships. Goal: Governance becomes a growth driver'] },
          { title: 'Future-Proof Risk & Compliance', sub: ['Implement enterprise-level: risk registers; compliance frameworks', 'Ensure: cross-border readiness', 'Build: succession planning; continuity frameworks. Goal: Long-term resilience at scale'] },
          { title: 'Scale Board & Advisory Power', sub: ['Add: global independent directors', 'Expand: advisory councils (growth, ESG, tech)', 'Create governance committees: audit; compensation; ESG. Goal: Strategic leadership at global level'] },
        ]},
      ]},
      { title: 'What This Means for You', blocks: [
        { kind: 'paragraph', text: 'You are no longer managing governance. You are setting the standard' },
        { kind: 'paragraph', text: 'Your company is now: trusted by investors; attractive to partners; stable for employees; credible globally' },
        { kind: 'paragraph', text: 'As stated in your report: "You are not avoiding red flags — you are setting the gold standard"' },
        { kind: 'paragraph', text: 'Your next challenge: Maintain excellence while scaling complexity' },
        { kind: 'callout', text: 'Your evolution: From "institutional company" to "global category leader"' },
      ]},
      { title: 'Your Governance Evolution Path', blocks: [
        { kind: 'path', steps: ['Governance Leader', 'Governance Champion', 'Global Governance Leader', 'Category Dominance'], currentIndex: 1 },
      ]},
      { title: 'Recommended Next Step', blocks: [
        { kind: 'paragraph', text: 'Recommended Path: Global Scaling & Strategic Capital' },
        { kind: 'callout', text: 'Global Scaling & Strategic Capital' },
        { kind: 'paragraph', text: 'We help you:' },
        { kind: 'bullets', items: [
          'leverage governance for global expansion',
          'structure large-scale fundraising (Series B+)',
          'align board and strategy globally',
          'position you as category leader',
        ]},
        { kind: 'paragraph', text: 'Outcome: From world-class company → global leader' },
      ]},
      { title: 'Immediate Action Plan', blocks: [
        { kind: 'bullets', items: [
          'Integrate governance into investor deck',
          'Publish ESG/governance report',
          'Expand board with global experts',
          'Build governance committees',
          'Implement enterprise risk framework',
        ]},
      ]},
      { title: 'Final CTA', blocks: [
        { kind: 'callout', text: 'Use governance excellence as your competitive moat to dominate your category', tone: 'green' },
      ]},
    ],
  },
];
